// Gallery for Murano lamp product pages (desktop).
//
// - MuranoHeroGallery: the main image with arrows, a counter and a thumbnail
//   row, so every photo can be browsed next to the buy form.
// - MuranoFilmstrip: an editorial "Up close" strip further down the page that
//   replaces the plain wall of images. Scroll-snapped, with arrows and a thin
//   progress line in the same style as the quizzes.
//
// Mobile keeps the existing swipe carousel on the product page.

import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, ZoomIn } from "lucide-react";

type Img = { url: string; altText: string | null };

/** Murano lamps of every kind (ceiling, wall, table, chandelier); not accessories. */
export function isMuranoLamp(productType: string | null | undefined): boolean {
  const t = (productType ?? "").toLowerCase();
  return t.startsWith("murano") && !t.includes("tilbehør");
}

const pad = (n: number) => String(n).padStart(2, "0");

function thumbUrl(url: string, width: number) {
  return url.includes("cdn.shopify.com")
    ? `${url}${url.includes("?") ? "&" : "?"}width=${width}`
    : url;
}

const arrowBtn =
  "flex h-10 w-10 items-center justify-center rounded-full bg-background/85 text-foreground shadow-sm backdrop-blur transition-all hover:bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-terracotta";

export function MuranoHeroGallery({
  images,
  index,
  onIndexChange,
  onZoom,
  title,
  zoomLabel,
  locale,
}: {
  images: Img[];
  index: number;
  onIndexChange: (i: number) => void;
  onZoom: () => void;
  title: string;
  zoomLabel: string;
  locale: "da" | "en";
}) {
  const da = locale === "da";
  const hero = images[index];
  const many = images.length > 1;
  const go = (d: number) => onIndexChange((index + d + images.length) % images.length);

  // Keep the active thumbnail visible when stepping with arrows or keys.
  const stripRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = stripRef.current?.children[index] as HTMLElement | undefined;
    el?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
  }, [index]);

  return (
    <div className="hidden md:block">
      <div className="group relative aspect-[4/5] w-full overflow-hidden rounded-sm bg-muted">
        <button
          type="button"
          onClick={onZoom}
          className="absolute inset-0 block h-full w-full"
          aria-label={zoomLabel}
        >
          {hero && (
            <img
              key={hero.url}
              src={hero.url}
              alt={hero.altText ?? title}
              fetchPriority="high"
              className="h-full w-full animate-in fade-in object-cover duration-500 transition-transform ease-out group-hover:scale-[1.015]"
            />
          )}
        </button>

        <span className="pointer-events-none absolute right-4 top-4 inline-flex items-center gap-1.5 rounded-full bg-background/85 px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] text-foreground/80 opacity-0 transition-opacity group-hover:opacity-100">
          <ZoomIn className="h-3 w-3" /> {zoomLabel}
        </span>

        {many && (
          <>
            <div className="pointer-events-none absolute inset-x-4 top-1/2 flex -translate-y-1/2 justify-between opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
              <button
                type="button"
                onClick={() => go(-1)}
                aria-label={da ? "Forrige billede" : "Previous image"}
                className={`pointer-events-auto ${arrowBtn}`}
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => go(1)}
                aria-label={da ? "Næste billede" : "Next image"}
                className={`pointer-events-auto ${arrowBtn}`}
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
            <span
              aria-hidden
              className="pointer-events-none absolute bottom-4 left-4 rounded-full bg-background/85 px-2.5 py-1 font-display text-[13px] tabular-nums text-foreground/80 backdrop-blur"
            >
              {pad(index + 1)} <span className="text-foreground/40">/ {pad(images.length)}</span>
            </span>
          </>
        )}
      </div>

      {many && (
        <div
          ref={stripRef}
          className="mt-3 flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {images.map((im, i) => (
            <button
              key={im.url}
              type="button"
              onClick={() => onIndexChange(i)}
              aria-label={`${da ? "Billede" : "Image"} ${i + 1}`}
              aria-current={i === index}
              className={`relative aspect-[4/5] w-[calc((100%-2.5rem)/6)] shrink-0 overflow-hidden rounded-sm bg-muted transition-opacity duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-terracotta ${
                i === index ? "opacity-100" : "opacity-55 hover:opacity-100"
              }`}
            >
              <img
                src={thumbUrl(im.url, 200)}
                alt=""
                loading="lazy"
                decoding="async"
                className="h-full w-full object-cover"
              />
              <span
                aria-hidden
                className={`absolute inset-x-0 bottom-0 h-0.5 transition-colors ${
                  i === index ? "bg-foreground" : "bg-transparent"
                }`}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function MuranoFilmstrip({
  images,
  onOpen,
  title,
  locale,
}: {
  images: Img[];
  /** Index into `images` */
  onOpen: (i: number) => void;
  title: string;
  locale: "da" | "en";
}) {
  const da = locale === "da";
  const trackRef = useRef<HTMLDivElement>(null);
  const [progress, setProgress] = useState(0);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const update = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setProgress(max > 0 ? el.scrollLeft / max : 1);
    setAtStart(el.scrollLeft <= 4);
    setAtEnd(el.scrollLeft >= max - 4);
  }, []);

  useEffect(() => {
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [update, images.length]);

  const scrollBy = (dir: number) => {
    const el = trackRef.current;
    if (!el) return;
    const item = el.firstElementChild as HTMLElement | null;
    const step = item ? item.offsetWidth + 24 : el.clientWidth * 0.8;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  if (images.length === 0) return null;
  const visibleShare = Math.min(1, 3 / images.length);

  return (
    <section className="hidden pb-20 md:block md:pb-28">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="flex items-end justify-between gap-8">
          <div>
            <p className="eyebrow">{da ? "Tæt på" : "Up close"}</p>
            <h2 className="mt-4 font-display text-[clamp(1.9rem,4vw,3rem)] leading-[1.02] tracking-tight">
              {da ? (
                <Fragment>
                  Set fra alle <em className="text-terracotta">vinkler</em>
                </Fragment>
              ) : (
                <Fragment>
                  From every <em className="text-terracotta">angle</em>
                </Fragment>
              )}
            </h2>
          </div>
          {images.length > 3 && (
            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                onClick={() => scrollBy(-1)}
                disabled={atStart}
                aria-label={da ? "Forrige billeder" : "Previous images"}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-border text-foreground transition-colors hover:border-foreground disabled:opacity-35 disabled:hover:border-border"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => scrollBy(1)}
                disabled={atEnd}
                aria-label={da ? "Næste billeder" : "Next images"}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-border text-foreground transition-colors hover:border-foreground disabled:opacity-35 disabled:hover:border-border"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>

        <div
          ref={trackRef}
          onScroll={update}
          className="mt-10 flex snap-x snap-mandatory gap-6 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {images.map((im, i) => (
            <button
              key={im.url}
              type="button"
              onClick={() => onOpen(i)}
              className="group relative aspect-[4/5] w-[calc((100%-3rem)/3)] shrink-0 snap-start overflow-hidden rounded-sm bg-muted"
            >
              <img
                src={thumbUrl(im.url, 900)}
                alt={im.altText ?? title}
                loading="lazy"
                decoding="async"
                className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
              />
              <span
                aria-hidden
                className="absolute bottom-3 left-3 rounded-full bg-background/85 px-2 py-0.5 font-display text-[12px] tabular-nums text-foreground/75 opacity-0 backdrop-blur transition-opacity group-hover:opacity-100"
              >
                {pad(i + 1)}
              </span>
            </button>
          ))}
        </div>

        {images.length > 3 && (
          <div className="relative mt-6 h-px w-full bg-border" aria-hidden>
            <span
              className="absolute inset-y-0 bg-foreground transition-[left] duration-200"
              style={{
                width: `${visibleShare * 100}%`,
                left: `${progress * (1 - visibleShare) * 100}%`,
              }}
            />
          </div>
        )}
      </div>
    </section>
  );
}
